import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@eti/components';
import { css, cx, etiColors, injectGlobal, mediaQueries } from '@eti/styles';
import { ResultFilter } from '@eti/types';
import { X } from '@phosphor-icons/react';
import { useToggle } from '../../../../../common/custom-hooks/toggle';
import { SelectedFilters } from '../../../constants/resultPropTypes';
import DebugFilters from './DebugFilters';
import SortTypes from './SortTypes';

const debugToolsWrapperStyles = css`
  backdrop-filter: blur(2px);
  background-color: rgb(240 240 240 / 90%);
  bottom: 0;
  display: flex;
  flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica', 'Arial', sans-serif;
  -webkit-font-smoothing: subpixel-antialiased;
  max-width: 90%;
  min-width: 400px;
  position: fixed;
  right: 100%;
  top: 0;
  transition: transform 0.5s ease-in-out;
  z-index: 998;

  &.open {
    box-shadow: 1px 0 9px 3px rgb(90 90 90 / 50%);
    transform: translate(100%);
    z-index: 999;
  }

  @media ${mediaQueries.xlarge.up} {
    width: ${`calc(100% - 1170px)`};
  }

  @media (min-width: 1970px) {
    width: ${`calc(50% - 1170px / 2)`};
  }
`;

const toggleButtonClass = css`
  backdrop-filter: blur(2px);
  border-bottom: 1px solid ${etiColors.grayScale[9]};
  border-bottom-right-radius: 3px;
  border-left: none;
  border-right: 1px solid ${etiColors.grayScale[9]};
  border-top: 1px solid ${etiColors.grayScale[9]};
  border-top-right-radius: 3px;
  bottom: 225px;
  cursor: pointer;
  padding: 10px 10px 4px;
  position: absolute;
  right: -70px;
  width: 70px;

  @media ${mediaQueries.medium.up} {
    bottom: 185px;
  }

  &:focus {
    outline: none;
  }

  & svg {
    pointer-events: none;
  }
`;

const closeButtonStyles = css`
  background: transparent;
  border: 0;
  cursor: pointer;
  display: flex;
  padding: 8px;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 2;

  [dir='rtl'] & {
    left: 0;
    right: auto;
  }

  & svg {
    fill: #333;
  }

  &:hover svg {
    fill: #262626;
  }

  &:focus {
    outline: 0;
  }
`;

const injectGlobalStyles = () => {
  /* stylelint-disable */
  // eslint-disable-next-line no-unused-expressions
  injectGlobal`
    .resultPageDebugTools.devBarOpen .etiContentWrapper {
      @media ${mediaQueries.xlarge.up} {
        margin-left: calc(100% - 1170px);
      }
      @media (min-width: 1970px) {
        margin-left: auto;
      }
    }
  `;
  /* stylelint-enable */
};

const ResultPageDebugTools = ({
  availableSortTypes,
  debugFilters = [],
  filteredFlightsCount,
  flightsCount,
  onFilter,
  onResetAll,
  onSort,
  selectedFilters,
  selectedSortType,
}) => {
  const { isOpen, handleToggle, handleClose } = useToggle(false);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('resultPageDebugTools');
      document.body.classList.add('devBarOpen');
    } else if (document.body.classList.contains('resultPageDebugTools')) {
      document.body.classList.remove('resultPageDebugTools');
      document.body.classList.remove('devBarOpen');
    }
  }, [isOpen]);

  useEffect(() => {
    injectGlobalStyles();
  }, []);

  return (
    <div
      className={cx(debugToolsWrapperStyles, `${isOpen ? 'open' : 'closed'}`)}
      data-testid="resultPage-debugTools-panel"
    >
      <button
        aria-label="Close debug drawer"
        className={closeButtonStyles}
        data-testid="resultPage-debugTools-closeButton-button"
        onClick={handleClose}
        type="button"
      >
        <X aria-hidden className="etiModal__closeIcon" size="22px" />
      </button>
      <SortTypes
        availableSortTypes={availableSortTypes}
        onSort={onSort}
        selectedSortType={selectedSortType}
      />
      <DebugFilters
        availableFilters={debugFilters}
        filteredFlightsCount={filteredFlightsCount}
        flightsCount={flightsCount}
        onFilter={onFilter}
        onResetAll={onResetAll}
        selectedFilters={selectedFilters}
      />
      <button
        className={toggleButtonClass}
        data-testid="resultPage-debugTools-toggleButton-button"
        onClick={handleToggle}
        type="button"
      >
        <Icon color="#307be8" id="filter" size="1rem" />
        <div>Filters</div>
      </button>
    </div>
  );
};

ResultPageDebugTools.propTypes = {
  availableSortTypes: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string,
      name: PropTypes.string,
    }),
  ),
  debugFilters: PropTypes.arrayOf(ResultFilter),
  filteredFlightsCount: PropTypes.number,
  flightsCount: PropTypes.number,
  onFilter: PropTypes.func,
  onResetAll: PropTypes.func,
  onSort: PropTypes.func,
  selectedFilters: SelectedFilters,
  selectedSortType: PropTypes.string,
};

export default ResultPageDebugTools;
